import { useEffect, useId, useRef, useState, type Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { useSaveOrderPurchaseOrderNotes } from '~/api';
import { Button } from '~/components/ui';
import { TextArea } from '~/components/ui/inputs/TextArea';
import { LabeledCheckbox } from '~/components/common/LabeledCheckbox';
import { useEscapeToClose } from '~/hooks/useEscapeToClose';
import type { MsrPurchaseOrderPrintOptions } from '~/utils/msrPurchaseOrderUrl';

const PDF_OPTION_LABEL_KEYS: Record<
  keyof MsrPurchaseOrderPrintOptions,
  string
> = {
  prices: 'order.purchase_order_print_options.prices',
  shippingInstructions:
    'order.purchase_order_print_options.shipping_instructions',
  weight: 'order.purchase_order_print_options.weight',
  shipTo: 'order.purchase_order_print_options.ship_to',
};

const PURCHASE_ORDER_NOTES_MAX_LENGTH = 2000;

export function MsrPurchaseOrderPrintOptionsPanel({
  id,
  ref,
  orderId,
  purchaseOrderNotes,
  purchaseOrderNotesFromMsr,
  printOptions,
  onChange,
  printTally,
  onPrintTallyChange,
  onViewPdf,
  onClose,
}: Readonly<{
  id: string;
  ref?: Ref<HTMLDivElement>;
  orderId: string;
  purchaseOrderNotes: string;
  purchaseOrderNotesFromMsr: boolean;
  printOptions: MsrPurchaseOrderPrintOptions;
  onChange: (printOptions: MsrPurchaseOrderPrintOptions) => void;
  printTally: boolean;
  onPrintTallyChange: (printTally: boolean) => void;
  onViewPdf: () => void;
  onClose: () => void;
}>) {
  const { t } = useTranslation('order');
  const titleId = useId();
  const firstOptionRef = useRef<HTMLInputElement>(null);
  const notesId = useId();
  const notesHelpId = useId();
  const [notesDraft, setNotesDraft] = useState<string | null>(null);
  if (purchaseOrderNotesFromMsr && notesDraft !== null) {
    setNotesDraft(null);
  }
  const shownNotes = notesDraft ?? purchaseOrderNotes;
  const { mutate: saveNotes, isPending: isSavingNotes } =
    useSaveOrderPurchaseOrderNotes();
  useEscapeToClose({ isOpen: true, onClose });

  useEffect(() => {
    firstOptionRef.current?.focus();
  }, []);

  const pdfOption = (
    name: keyof MsrPurchaseOrderPrintOptions,
    inputRef?: Ref<HTMLInputElement>
  ) => (
    <LabeledCheckbox
      ref={inputRef}
      label={t(PDF_OPTION_LABEL_KEYS[name])}
      checked={printOptions[name]}
      onChange={(checked) => onChange({ ...printOptions, [name]: checked })}
    />
  );

  return (
    <div
      id={id}
      ref={ref}
      role="dialog"
      aria-labelledby={titleId}
      className="mt-2 w-64 bg-neut-bg-alt rounded-lg border border-neut-border p-3 shadow-xl"
      data-testid="purchase-order-print-options"
    >
      <p id={titleId} className="text-xs text-neut-500 mb-2">
        {t('order.purchase_order_print_options.title')}
      </p>
      <div className="flex flex-col gap-2 mb-3">
        {pdfOption('prices', firstOptionRef)}
        <LabeledCheckbox
          label={t('order.purchase_order_print_options.tally')}
          checked={printTally}
          onChange={onPrintTallyChange}
        />
        {pdfOption('shippingInstructions')}
        {pdfOption('weight')}
        {pdfOption('shipTo')}
      </div>
      <div className="flex flex-col gap-1 mb-3">
        <TextArea
          id={notesId}
          name="purchaseOrderNotes"
          label={t('order.purchase_order_print_options.notes_label')}
          value={shownNotes}
          onChange={(_, value) =>
            setNotesDraft(value === purchaseOrderNotes ? null : value)
          }
          rows={3}
          maxLength={PURCHASE_ORDER_NOTES_MAX_LENGTH}
          readOnly={purchaseOrderNotesFromMsr || isSavingNotes}
          describedBy={notesHelpId}
        />
        {purchaseOrderNotesFromMsr ? (
          <p id={notesHelpId} className="text-xs text-neut-500">
            {t('order.purchase_order_print_options.notes_from_msr')}
          </p>
        ) : (
          <div className="flex items-center justify-between gap-2">
            <span id={notesHelpId} className="text-xs text-neut-500">
              {shownNotes.length}/{PURCHASE_ORDER_NOTES_MAX_LENGTH}
            </span>
            <Button
              size="sm"
              width="auto"
              variant="secondary"
              loading={isSavingNotes}
              disabled={shownNotes.trim() === purchaseOrderNotes.trim()}
              onClick={() =>
                saveNotes(
                  { orderId, notes: shownNotes },
                  { onSuccess: () => setNotesDraft(null) }
                )
              }
              data-testid="save-purchase-order-notes-button"
            >
              {t('order.purchase_order_print_options.save_notes_button')}
            </Button>
          </div>
        )}
      </div>
      <Button
        size="sm"
        onClick={onViewPdf}
        data-testid="view-purchase-order-pdf-button"
      >
        {t('order.purchase_order_print_options.view_pdf_button')}
      </Button>
    </div>
  );
}
